<template>
  <mf-card-container v-model="search" title="Resultado por produto" useSearch placeholder="Buscar produto">
    <template v-slot:filters>
      <export-data :data-fetcher="getDataToExport" filename="resultados_por_produto" ghost />
      <headers-data-table-settings
        :key="headersSettingsName"
        :headers.sync="headers"
        :default-headers="getDefaultHeadersOptions()"
        :headers-settings-name="headersSettingsName"
      />
    </template>
    <v-data-table
      :items="currentItems"
      :headers="computedHeaders"
      :search="search"
      sort-by="name"
      :sort-desc="[false]"
      hide-default-footer
      disable-pagination
      no-data-text="Não há produtos nesta campanha"
      class="mt-4"
    >
      <template v-slot:header.quantity="{ header }">
        <mf-tooltip top variant="secondary" max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Total de vendas dos SKU dentro da oferta no período.</span>
        </mf-tooltip>
      </template>

      <template v-slot:item.revenue="{ item, header }">
        <div class="value-with-stonks">
          <span class="min-width-84">{{ header.formater(item.revenue) }}</span>
          <tooltip
            v-if="item.revenue !== 0 || item.previousValue.revenue !== 0"
            align="top"
            max-width="192px"
            variant="secondary"
            :message="item.tooltip.revenue"
          >
            <mf-stonks
              variant="percentage"
              :show-only-icon="item.revenue === 0 || item.previousValue.revenue === 0"
              :percentage="item.percentage.revenue"
              bold
              hide-percentage-decimal
            />
          </tooltip>
        </div>
      </template>

      <template v-slot:item.quantity="{ item, header }">
        <div class="value-with-stonks">
          <span class="min-width-36">{{ header.formater(item.quantity) }}</span>
          <tooltip
            v-if="item.quantity !== 0 || item.previousValue.quantity !== 0"
            align="top"
            max-width="192px"
            variant="secondary"
            :message="item.tooltip.quantity"
          >
            <mf-stonks
              variant="percentage"
              :show-only-icon="item.quantity === 0 || item.previousValue.quantity === 0"
              :percentage="item.percentage.quantity"
              bold
              hide-percentage-decimal
            />
          </tooltip>
        </div>
      </template>

      <template v-slot:item.clients="{ item, header }">
        <div class="value-with-stonks">
          <span class="min-width-36">{{ header.formater(item.clients) }}</span>
          <tooltip
            v-if="item.clients !== 0 || item.previousValue.clients !== 0"
            align="top"
            max-width="192px"
            variant="secondary"
            :message="item.tooltip.clients"
          >
            <mf-stonks
              variant="percentage"
              :show-only-icon="item.clients === 0 || item.previousValue.clients === 0"
              :percentage="item.percentage.clients"
              bold
              hide-percentage-decimal
            />
          </tooltip>
        </div>
      </template>

      <template v-slot:item.tickets="{ item, header }">
        <div class="value-with-stonks">
          <span class="min-width-36">{{ header.formater(item.tickets) }}</span>
          <tooltip
            v-if="item.tickets !== 0 || item.previousValue.tickets !== 0"
            align="top"
            max-width="192px"
            variant="secondary"
            :message="item.tooltip.tickets"
          >
            <mf-stonks
              variant="percentage"
              :show-only-icon="item.tickets === 0 || item.previousValue.tickets === 0"
              :percentage="item.percentage.tickets"
              bold
              hide-percentage-decimal
            />
          </tooltip>
        </div>
      </template>

      <template v-slot:item.averageTicket="{ item, header }">
        <div class="value-with-stonks">
          <span class="min-width-64">{{ header.formater(item.averageTicket) }}</span>
          <tooltip
            v-if="item.averageTicket !== 0 || item.previousValue.averageTicket !== 0"
            align="top"
            max-width="192px"
            variant="secondary"
            :message="item.tooltip.averageTicket"
          >
            <mf-stonks
              variant="percentage"
              :show-only-icon="item.averageTicket === 0 || item.previousValue.averageTicket === 0"
              :percentage="item.percentage.averageTicket"
              bold
              hide-percentage-decimal
            />
          </tooltip>
        </div>
      </template>

      <template v-if="currentItems.length > 0" v-slot:body.append="{ headers }">
        <tr class="total-row">
          <td v-for="(header, i) in headers" :key="i">
            <div v-if="header.value === 'sku'">
              <mf-tooltip top max-width="192px">
                <template v-slot:activator="{ on }">
                  <div class="d-flex align-center">
                    <span>Total:</span>
                    <mf-icon class="ml-1" small color="cinza-0" v-on="on"> info </mf-icon>
                  </div>
                </template>
                <span>
                  O total é calculado considerando clientes e cupons únicos. Um mesmo cliente pode comprar diferentes produtos, e um produto pode aparecer em
                  diversos cupons. Por isso a soma dos valores da tabela pode não ser igual ao total.
                </span>
              </mf-tooltip>
            </div>

            <div v-if="header.value === 'name'"></div>

            <div v-if="header.value === 'revenue'">
              {{ localeString(itemsTotal.revenue, 2) | currency }}
            </div>

            <div v-if="header.value === 'quantity'">
              {{ localeString(itemsTotal.quantity, 0) }}
            </div>

            <div v-if="header.value === 'clients'">
              {{ localeString(itemsTotal.clients, 0) }}
            </div>

            <div v-if="header.value === 'tickets'">
              {{ localeString(itemsTotal.tickets, 0) }}
            </div>

            <div v-if="header.value === 'averageTicket'">
              {{ localeString(itemsTotal.averageTicket, 2) | currency }}
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </mf-card-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { localeString } from '@/config/helpers.js'

export default {
  name: 'BenefitTriggerProductsTable',
  components: {
    ExportData: () => import('@/components/export-data/ExportData.vue'),
    HeadersDataTableSettings: () => import('@/components/HeadersDataTableSettings.vue'),
    MfStonks: () => import('@/modules/maria/produtos-impulsionadores/views/components/Step1/MfStonks.vue'),
    Tooltip: () => import('@/modules/campanhas/gestao-ofertas-v2/views/components/Tooltip.vue'),
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    currentMetrics: {
      type: Object,
      default: () => ({}),
    },
    previousMetrics: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    search: '',
    headers: [],
  }),
  computed: {
    defaultHeaders() {
      return [
        {
          text: 'Código',
          value: 'sku',
          sortable: true,
          filterable: true,
          isActive: true,
          isRequired: true,
          isDefault: true,
          formater: (value) => value,
        },
        {
          text: 'Nome',
          value: 'name',
          sortable: true,
          filterable: true,
          isActive: true,
          isRequired: true,
          isDefault: true,
          formater: (value) => value,
        },
        {
          text: 'Faturamento',
          value: 'revenue',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          formater: (value) => `R$ ${localeString(value, 2)}`,
        },
        {
          text: 'Unidades vendidas',
          value: 'quantity',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          formater: (value) => localeString(value, 0),
        },
        {
          text: 'Clientes que compraram',
          value: 'clients',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          formater: (value) => localeString(value, 0),
        },
        {
          text: 'Cupons fiscais',
          value: 'tickets',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          formater: (value) => localeString(value, 0),
        },
        {
          text: 'Ticket médio',
          value: 'averageTicket',
          money: true,
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          formater: (value) => `R$ ${localeString(value, 2)}`,
        },
      ]
    },

    computedHeaders() {
      return this.headers.filter((header) => header.isActive)
    },

    headersSettingsName() {
      return 'benefit-trigger-products-th'
    },

    previousItems() {
      return this.fillItems(this.previousMetrics)
    },

    currentItems() {
      return this.fillItems(this.currentMetrics).map((item) => {
        return {
          ...item,
          percentage: {
            revenue: this.calcPercentage('revenue', item),
            quantity: this.calcPercentage('quantity', item),
            clients: this.calcPercentage('clients', item),
            tickets: this.calcPercentage('tickets', item),
            averageTicket: this.calcPercentage('averageTicket', item),
          },
          previousValue: {
            revenue: this.previousValue('revenue', item),
            quantity: this.previousValue('quantity', item),
            clients: this.previousValue('clients', item),
            tickets: this.previousValue('tickets', item),
            averageTicket: this.previousValue('averageTicket', item),
          },
          tooltip: {
            revenue: this.percentageTooltip('revenue', item),
            quantity: this.percentageTooltip('quantity', item),
            clients: this.percentageTooltip('clients', item),
            tickets: this.percentageTooltip('tickets', item),
            averageTicket: this.percentageTooltip('averageTicket', item),
          },
        }
      })
    },

    itemsTotal() {
      return {
        revenue: this.currentMetrics?.products_metrics?.totals?.sales?.revenue || 0,
        quantity: this.currentMetrics?.products_metrics?.totals?.sales?.quantity || 0,
        clients: this.currentMetrics?.products_metrics?.totals?.sales?.clients || 0,
        tickets: this.currentMetrics?.products_metrics?.totals?.sales?.tickets || 0,
        averageTicket:
          this.currentMetrics?.products_metrics?.totals?.sales?.tickets > 0
            ? this.currentMetrics?.products_metrics?.totals?.sales?.revenue / this.currentMetrics?.products_metrics?.totals?.sales?.tickets
            : 0,
      }
    },
  },
  methods: {
    getDataToExport() {
      const headers = this.computedHeaders.map((header) => header.text)
      const rows = this.currentItems.map((item) => {
        return this.computedHeaders.map((header) => {
          return header.formater(item[header.value])
        })
      })
      return { headers, rows }
    },

    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },

    previousValue(fieldName, currentItem) {
      const previousValue = this.previousItems.find((item) => item.sku === currentItem.sku)
      if (!previousValue) return 0
      return previousValue[fieldName]
    },

    calcPercentage(fieldName, currentItem) {
      const previousValue = this.previousValue(fieldName, currentItem)
      const currentValue = currentItem[fieldName]
      if (!previousValue || !currentValue) {
        return this.calcPositiveOrNegativeVariation(previousValue, currentValue)
      }
      return ((currentValue - previousValue) * 100) / previousValue
    },

    calcPositiveOrNegativeVariation(previousValue, currentValue) {
      if (previousValue > currentValue) return -100
      return 100
    },

    percentageTooltip(fieldName, currentItem) {
      const previousValue = this.previousValue(fieldName, currentItem)
      if (previousValue === 0 || currentItem[fieldName] === 0) {
        const percentage = this.calcPercentage(fieldName, currentItem) > 0
        return `Esse valor ${percentage > 0 ? 'aumentou' : 'diminuiu'} comparado com o mês anterior, mas não pode ser calculado pois o mês ${
          percentage > 0 ? 'anterior' : 'atual'
        } tem valor 0.`
      }
      return 'Comparado com o mês anterior.'
    },

    fillItems(metrics) {
      const items = []
      metrics?.products_metrics?.table?.forEach((item) => {
        items.push({
          sku: item.product.sku,
          name: item.product.name,
          revenue: item.sales.revenue,
          quantity: item.sales.quantity,
          clients: item.sales.clients,
          tickets: item.sales.tickets,
          averageTicket: item.sales.tickets > 0 ? item.sales.revenue / item.sales.tickets : 0,
        })
      })

      const itemsSku = items.map((item) => item.sku)
      const missingProducts = this.products.filter((product) => !itemsSku.includes(product.sku))
      missingProducts.forEach((product) => {
        items.push({
          sku: product.sku,
          name: product.name,
          revenue: 0,
          quantity: 0,
          clients: 0,
          tickets: 0,
          averageTicket: 0,
        })
      })

      return items
    },
  },
}
</script>

<style lang="scss" scoped>
.value-with-stonks {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
}

.min-width-84 {
  min-width: 84px;
}

.min-width-64 {
  min-width: 64px;
}

.min-width-36 {
  min-width: 36px;
}

.total-row {
  background-color: $cinza-l90;
}
</style>
